import { memo } from "react";
import { STATE_STYLE, SEGMENT_STYLE, canViewAI, isSuperStore, fmtDate } from "../lib/states";
import { useAuth } from "../context/AuthContext";
import { Calendar, User, Flame, Wrench, Star, Handshake } from "lucide-react";

// Compact activity indicator: single-icon + ≤4-char relative timestamp.
// Colour encodes recency; null (never contacted) renders as a faded, textless
// icon so untouched leads at the top of a work-queue column read as "empty".
const ACT_MS = { day: 24 * 3600 * 1000 };
function activityStyle(iso) {
  if (!iso) return { chip: "text-stone-400 opacity-40", label: "", full: "Never contacted" };
  const then = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.max(0, (now - then) / 60000);
  const hrs = mins / 60;
  const days = hrs / 24;
  let label;
  if (hrs < 1) label = "now";
  else if (hrs < 24) label = `${Math.floor(hrs)}h`;
  else if (days <= 30) label = `${Math.floor(days)}d`;
  else label = `${Math.floor(days / 30)}mo`;
  const chip =
    hrs < 24 ? "text-emerald-600" :
    days <= 7 ? "text-amber-600" :
    "text-red-600";
  const full = new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return { chip, label, full: `Last activity: ${full}` };
}

function LeadCard({ lead, showAssignee, onClick, onDragStart, draggable = true }) {
  const { user } = useAuth();
  const style = STATE_STYLE[lead.state] || STATE_STYLE.Fresh;
  const segStyle = SEGMENT_STYLE[lead.segment] || SEGMENT_STYLE.Unknown;
  const showSeg = lead.segment && lead.segment !== "Unknown" && canViewAI(user);
  const showStore = isSuperStore(user) && lead.store;
  // AI Star is an AI-derived flag — same visibility rule as the AI segment.
  const showAiStar = lead.ai_suggested_hot && canViewAI(user);
  const act = activityStyle(lead.last_activity_at);

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart ? (e) => onDragStart(e, lead) : undefined}
      onClick={() => onClick(lead)}
      data-testid={`lead-card-${lead.id}`}
      className={`group p-3.5 bg-white rounded-lg border shadow-sm hover:shadow-md transition-all ${
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
      } ${lead.is_hot ? "border-red-300 ring-1 ring-red-200" : "border-black/5 hover:border-stone-300"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-stone-900 text-sm leading-snug truncate" data-testid={`lead-name-${lead.id}`}>
          {lead.name}
        </p>
        <div className="flex items-center gap-1 shrink-0 mt-0.5">
          {lead.is_hot && <Flame className="h-3.5 w-3.5 text-red-500" data-testid={`hot-flag-${lead.id}`} />}
          {showAiStar && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" data-testid={`ai-star-${lead.id}`} aria-label="AI Star — high potential" />}
          <span className={`h-2 w-2 rounded-full ${style.dot}`} />
        </div>
      </div>

      {showSeg && (
        <span className={`inline-flex items-center gap-1 mt-2 text-[10px] px-2 py-0.5 rounded-full border font-medium ${segStyle}`}>
          {lead.segment === "Trade / Architect" && <Wrench className="h-2.5 w-2.5" />}
          {lead.segment}
        </span>
      )}

      <div className="mt-2.5 flex items-center gap-1.5 text-xs text-stone-500">
        <Calendar className="h-3.5 w-3.5" />
        <span>{lead.next_action_date ? `Next: ${fmtDate(lead.next_action_date)}` : `Arrived ${fmtDate(lead.created_at)}`}</span>
        <span
          title={act.full}
          data-testid={`activity-dot-${lead.id}`}
          className={`ml-auto inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums ${act.chip}`}
        >
          <Handshake className="h-3.5 w-3.5" />
          {act.label && <span data-testid={`activity-label-${lead.id}`}>{act.label}</span>}
        </span>
      </div>

      {lead.also_in_jordyblue && isSuperStore(user) && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px]" data-testid={`jordyblue-flag-${lead.id}`}>
          <span className="px-1.5 py-0.5 rounded bg-stone-900 text-stone-100 border border-stone-900 font-medium tracking-wide uppercase text-[10px]">
            Also in JordyBlue
          </span>
        </div>
      )}

      {showStore && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px]" data-testid={`lead-store-${lead.id}`}>
          <span className="px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-700 font-medium tracking-wide uppercase text-[10px]">
            Store · {lead.store}
          </span>
        </div>
      )}

      {showAssignee && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-stone-400" data-testid={`lead-assignee-${lead.id}`}>
          <User className="h-3.5 w-3.5" />
          <span>{lead.assignee_name || "Unassigned"}</span>
          {lead.interaction_assignee_name && <span className="text-stone-300">· ◇ {lead.interaction_assignee_name}</span>}
        </div>
      )}
    </div>
  );
}

export default memo(LeadCard);
